import { useResourceParams } from '@refinedev/core';
import { createContext, ReactNode, useContext } from 'react';

import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { RESOURCES } from '@/Resources';

import { useUserContext } from './UserContext';

// ---- Permissions model ----
export type ResourcePermissionAction = 'list' | 'show' | 'create' | 'edit' | 'delete' | 'clone';
export type ResourcePermissions = Partial<Record<ResourcePermissionAction, boolean>>;

interface PermissionContextType {
  getPermissions: (resource: RESOURCES) => ResourcePermissions;
  hasPermission: (action: ResourcePermissionAction, res?: RESOURCES) => boolean;
  canAny: (resource: RESOURCES) => boolean;
  canAll: (resource: RESOURCES) => boolean;
}

const PermissionContext = createContext<PermissionContextType>({} as PermissionContextType);

export const PermissionProvider = ({ children }: { children: ReactNode }) => {
  const { resource } = useResourceParams();
  const { userWorkArea } = useUserContext();

  return (
    <PermissionContext.Provider
      value={{
        getPermissions: (resource: RESOURCES) => resolvePermissions(userWorkArea, resource),
        hasPermission: (action, res = resource?.name as RESOURCES) =>
          hasPerm(resolvePermissions(userWorkArea, res), action),
        canAny: resource => hasAnyPerm(resolvePermissions(userWorkArea, resource)),
        canAll: resource => hasAllPerm(resolvePermissions(userWorkArea, resource)),
      }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissionContext = () => useContext(PermissionContext);

// Helpers (non-exported) to safely resolve permissions
const emptyPermissions: ResourcePermissions = {
  list: false,
  show: false,
  create: false,
  edit: false,
  delete: false,
};

function resolvePermissions(
  workArea: USER_WORK_AREA | undefined,
  resource: RESOURCES,
): ResourcePermissions {
  if (!workArea) return emptyPermissions;
  const byResource = permissionsByWorkArea[workArea];

  if (!byResource) return emptyPermissions;

  return byResource[resource] ?? emptyPermissions;
}

function hasPerm(perms: ResourcePermissions, action: ResourcePermissionAction): boolean {
  return Boolean(perms[action]);
}

function hasAnyPerm(perms: ResourcePermissions): boolean {
  return Object.values(perms).some(a => Boolean(a));
}

function hasAllPerm(perms: ResourcePermissions): boolean {
  return Object.values(perms).every(a => Boolean(a));
}

const permissionsByWorkArea: Partial<
  Record<
    USER_WORK_AREA,
    Partial<
      Record<
        RESOURCES,
        { list?: boolean; show?: boolean; create?: boolean; edit?: boolean; delete?: boolean }
      >
    >
  >
> = {
  software: {
    [RESOURCES.TICKETS]: { list: true, show: true, create: true, edit: true, delete: false },
    [RESOURCES.SHIPMENTS]: { list: true, show: true, create: true, edit: true, delete: false },
    [RESOURCES.RECEPTIONS]: { list: true, show: true, create: true, edit: true, delete: false },
    [RESOURCES.CASE_RESOLUTION]: { list: true, show: true },
    [RESOURCES.SHIPPERS]: { list: true, show: true, create: true, edit: true, delete: false },
    [RESOURCES.SHIPPING_LABELS]: { list: true, show: true, create: true, edit: true, delete: true },
    [RESOURCES.SPARE_PARTS_COLLECTIONS]: {
      list: true,
      show: true,
      create: true,
      edit: true,
      delete: true,
    },
    [RESOURCES.SERVICE_ORDERS]: {
      list: true,
      show: true,
      create: true,
      edit: true,
      delete: true,
    },
  },
  logistics: {
    [RESOURCES.TICKETS]: { list: true, show: true, create: true, edit: true, delete: true },
    [RESOURCES.SHIPMENTS]: { list: true, show: true, create: true, edit: true, delete: true },
    [RESOURCES.RECEPTIONS]: { list: true, show: true, create: true, edit: true, delete: true },
    [RESOURCES.CASE_RESOLUTION]: { list: true, show: true },
    [RESOURCES.SHIPPERS]: { list: true, show: true, create: true, edit: true, delete: true },
    [RESOURCES.SHIPPING_LABELS]: { list: true, show: true, create: true, edit: true, delete: true },
    [RESOURCES.SERVICE_ORDERS]: {
      list: true,
      show: true,
    },
  },
  administration: {
    [RESOURCES.TICKETS]: { list: true, show: true, create: false, edit: false, delete: false },
    [RESOURCES.CASE_RESOLUTION]: { list: true, show: true },
    [RESOURCES.SHIPMENTS]: { list: true, show: true },
    [RESOURCES.RECEPTIONS]: { list: true, show: true },
    [RESOURCES.SPARE_PARTS_COLLECTIONS]: {
      list: true,
      show: true,
      create: false,
      edit: false,
      delete: false,
    },
    [RESOURCES.SERVICE_ORDERS]: {
      list: true,
      show: true,
    },
  },
  customerservice: {
    [RESOURCES.TICKETS]: { list: true, show: true, create: true, edit: true, delete: true },
    [RESOURCES.SHIPMENTS]: { list: true, show: true, create: true, edit: true, delete: true },
    [RESOURCES.RECEPTIONS]: { list: true, show: true, create: true, edit: true, delete: true },
    [RESOURCES.CASE_RESOLUTION]: { list: true, show: true },
    [RESOURCES.SHIPPERS]: { list: true, show: true, create: false, edit: false, delete: false },
    [RESOURCES.SHIPPING_LABELS]: { list: true, show: true, create: true, edit: true, delete: true },
    [RESOURCES.SERVICE_ORDERS]: {
      list: true,
      show: true,
      create: true,
      edit: true,
      delete: true,
    },
  },
  hardware: {
    [RESOURCES.TICKETS]: { list: true, show: true },
  },
  commercial: {
    [RESOURCES.TICKETS]: { list: true, show: true, create: true, edit: true, delete: true },
  },
  // marketing: {
  //   [RESOURCES.TICKETS]: { list: true, show: true },
  // },
  // repairs: {
  //   [RESOURCES.TICKETS]: { list: true, show: true },
  // },
};
